import React, { useState } from 'react';
import { Box, Button, MenuItem, TextField } from '@material-ui/core';
import { FixtureTimelineEvent } from '../TeamOverview/TeamOverviewModels';
import RoundOverviewUtil from '../RoundOverview/RoundOverviewUtil';
import { GOAL, MANUAL_EVENT_TYPES, MARKER_TYPES } from './TheGameStandingsUtil';

interface ManualEventFormProps {
    homeTeamName: string;
    awayTeamName: string;
    count: number;
    onAdd: (event: FixtureTimelineEvent) => void;
    onClear: () => void;
}

interface ManualEventFormState {
    type: string;
    minute: string;
    extraMinute: string;
    team: "HOME" | "AWAY";
    playerName: string;
    relatedPlayerName: string;
    result: string;
}

const parseMinute = (value: string, max: number): number | null | undefined => {
    if (value.trim().length === 0) {
        return null;
    }
    var n = Number(value.trim());
    return Number.isInteger(n) && n >= 0 && n <= max ? n : undefined;
};

// Client-only timeline events (typed or imported) for a source fixture the feed sent none for; nothing is saved.
const ManualEventForm: React.FC<ManualEventFormProps> = (props) => {
    const [form, setForm] = useState<ManualEventFormState>({
        type: GOAL, minute: "", extraMinute: "", team: "HOME", playerName: "", relatedPlayerName: "", result: ""
    });
    const [error, setError] = useState<string>("");
    const [nextId, setNextId] = useState<number>(1);

    const isMarker = MARKER_TYPES.includes(form.type);

    const handleChange = (name: keyof ManualEventFormState, value: string) => {
        setForm({ ...form, [name]: value });
        setError("");
    };

    const onAdd = () => {
        var minute = parseMinute(form.minute, 130);
        var extraMinute = parseMinute(form.extraMinute, 30);
        if (!isMarker && (minute === null || minute === undefined)) {
            setError("Minute 0-130 required");
            return;
        }
        if (!isMarker && extraMinute === undefined) {
            setError("Extra minute 0-30");
            return;
        }
        props.onAdd(isMarker
            ? { id: `manual-${nextId}`, type: form.type }
            : {
                id: `manual-${nextId}`,
                type: form.type,
                minute: minute,
                extraMinute: extraMinute || null,
                team: form.team,
                playerName: form.playerName.trim() || null,
                relatedPlayerName: form.relatedPlayerName.trim() || null,
                result: form.result.trim() || null
            });
        setNextId(nextId + 1);
        setForm({ ...form, minute: "", extraMinute: "", playerName: "", relatedPlayerName: "", result: "" });
    };

    return (
        <Box paddingTop={1}>
            <Box fontSize={12}>The feed has no timeline events for this fixture. Add events manually (only in this page, nothing is saved):</Box>
            <Box display="flex" alignItems="flex-end" flexWrap="wrap" style={{ gap: 8 }}>
                <TextField select label="Type" value={form.type} style={{ width: 160 }}
                           onChange={e => handleChange("type", e.target.value)}>
                    {MANUAL_EVENT_TYPES.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                </TextField>
                <TextField label="Min" value={form.minute} disabled={isMarker} style={{ width: 50 }}
                           onChange={e => handleChange("minute", e.target.value)} />
                <TextField label="+" value={form.extraMinute} disabled={isMarker} style={{ width: 40 }}
                           onChange={e => handleChange("extraMinute", e.target.value)} />
                <TextField select label="Team" value={form.team} disabled={isMarker} style={{ width: 160 }}
                           onChange={e => handleChange("team", e.target.value)}>
                    <MenuItem value="HOME">{props.homeTeamName}</MenuItem>
                    <MenuItem value="AWAY">{props.awayTeamName}</MenuItem>
                </TextField>
                <TextField label="Player" value={form.playerName} disabled={isMarker} style={{ width: 150 }}
                           onChange={e => handleChange("playerName", e.target.value)} />
                <TextField label="Related player" value={form.relatedPlayerName} disabled={isMarker} style={{ width: 150 }}
                           onChange={e => handleChange("relatedPlayerName", e.target.value)} />
                <TextField label="Result" placeholder="h-a" value={form.result} disabled={isMarker} style={{ width: 60 }}
                           onChange={e => handleChange("result", e.target.value)} />
                <Button variant="outlined" color="secondary" size="small" onClick={() => onAdd()}>Add event</Button>
                <Button variant="outlined" color="secondary" size="small" disabled={props.count === 0} onClick={() => props.onClear()}>
                    Clear all
                </Button>
            </Box>
            {error.length > 0 ? (<Box color={RoundOverviewUtil.INVALID_COLOR} fontSize={12}>{error}</Box>) : null}
        </Box>
    );
};

export default ManualEventForm;
